import { Analytics } from '@cobliteam/analytics';
import type { Checklist } from '@cobliteam/api-lib';
import { Location } from '@cobliteam/common-types';
import { AlertHookType } from '@cobliteam/hooks/src/useAlertHooks/types';
import useTriggerAlertHook from '@cobliteam/hooks/src/useAlertHooks/useTriggerAlertHook';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useConfirmBeforeLeave from '@cobliteam/hooks/src/useConfirmBeforeLeave';
import { createChecklist, getChecklistsNames, updateChecklist } from '@cobliteam/hooks/src/useEntity/useChecklist/useChecklist';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { useTranslation } from '@cobliteam/i18n';
import { SURVEY_KEYS, useSurveyTrigger } from '@cobliteam/surveys';
import { Button, Divider, Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@cobliteam/ui/components';
import Stepper, { StepperHorizontalTimelineHeader, useStepperContext } from '@cobliteam/ui/components/Stepper';
import { Form, useForm } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { AlertDialogCancel } from '@cobliteam/ui-business/components/AlertDialog';
import DraggableListContainer from '@cobliteam/ui-business/components/DraggableList/components/DraggableListContainer';
import { zodResolver } from '@hookform/resolvers/zod';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { createFormSchema, type FormValues } from './checklistSchema';
import { getChecklistTemplates } from './checklistTemplates';
import ChecklistItems from './components/ChecklistItems';
import ChecklistName from './components/ChecklistName';
import ChecklistSaveButton from './components/ChecklistSaveButton';
import ChecklistStatusToggle from './components/ChecklistStatusToggle';
import ChecklistTemplateStep from './components/ChecklistTemplateStep';
import ChecklistVehiclesFilters from './components/ChecklistVehiclesFilters';
import { checklistToFormValues, formValuesToChecklist } from './utils';

type DrawerStep = 'choose' | 'form';

interface ChecklistDrawerProps {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  checklist?: Checklist;
  hideTrigger?: boolean;
  /** Called after a checklist is successfully created or edited (e.g. to advance a guided flow). */
  onSuccess?: () => void;
  /** Skips the post-creation satisfaction survey (e.g. when opened from a guided onboarding flow). */
  disableSurvey?: boolean;
}

const StepperController = ({ id }: { id: string }) => {
  const { goTo } = useStepperContext();
  useEffect(() => {
    goTo(id);
  }, [id, goTo]);
  return null;
};

const ChecklistDrawer = ({ open, setOpen, checklist, hideTrigger, onSuccess, disableSurvey }: ChecklistDrawerProps) => {
  const { t } = useTranslation('checklists');
  const history = useHistory();
  const { setPosition } = useChatWidget();
  const { user } = useUser();
  const { triggerSurvey } = useSurveyTrigger();
  const hasGroupsRestriction = Boolean(user?.groupsRestriction?.length);

  const { data: checklistsNames } = getChecklistsNames();
  const { mutateAsync: addChecklist, isSuccess: isAddChecklistSuccess, reset: resetAddChecklist } = createChecklist();
  const { mutateAsync: editChecklist, isSuccess: isEditChecklistSuccess, reset: resetEditChecklist } = updateChecklist();
  const { triggerAlertHook } = useTriggerAlertHook();

  const [templateParam, setTemplateParam] = useQueryState('template', parseAsString);
  const templates = useMemo(() => getChecklistTemplates(t), [t]);

  const [step, setStep] = useState<DrawerStep>(checklist ? 'form' : 'choose');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [showAlertDialogCancel, setShowAlertDialogCancel] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(createFormSchema(hasGroupsRestriction)),
    defaultValues: checklistToFormValues(checklist),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: só reage à abertura
  useEffect(() => {
    if (!open || checklist) return;

    if (templateParam) {
      const template = templates.find((tpl) => tpl.id === templateParam);
      if (template) {
        setSelectedTemplateId(templateParam);
        const id = setTimeout(() => {
          form.reset({ ...template.values, active: true, vehicleIds: [], vehicleGroupIds: [] }, { keepDefaultValues: true });
        }, 0);
        return () => clearTimeout(id);
      }
    }

    setStep('choose');
    setSelectedTemplateId(null);
    form.reset(checklistToFormValues());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- só reage à abertura

  const handleTemplateSelect = (templateId: string | null) => {
    if (templateId) {
      const template = templates.find((tpl) => tpl.id === templateId);
      if (template) {
        form.reset({ ...template.values, active: true, vehicleIds: [], vehicleGroupIds: [] }, { keepDefaultValues: true });
        setTemplateParam(templateId);
      }
    } else {
      form.reset(checklistToFormValues());
      setTemplateParam(null);
    }
    setStep('form');
  };

  const handleSubmit = async (values: FormValues) => {
    Analytics.track('Checklist Drawer Save Button Clicked', {
      mode: checklist ? 'edit' : 'create',
      values: {
        name: values.name,
        items: values.items.map((item) => item.title),
      },
      template: selectedTemplateId ?? null,
    });

    const lowerName = values.name.toLowerCase();
    const hasDuplicatedName = checklistsNames?.names.some((name) => name.toLowerCase() === lowerName);

    if (hasDuplicatedName && checklist?.name?.toLowerCase() !== lowerName) {
      form.setError('name', {
        type: 'manual',
        message: t('duplicate_name_error'),
      });
      return;
    }

    const newChecklist = formValuesToChecklist(values, !checklist);

    const result = checklist ? await editChecklist({ id: checklist.id, updatedChecklist: newChecklist }) : await addChecklist({ ...newChecklist, template: !checklist });

    void triggerAlertHook(AlertHookType.CHECKLIST, {});

    return result;
  };

  useEffect(() => {
    if (isAddChecklistSuccess || isEditChecklistSuccess) {
      form.reset(checklist ? form.getValues() : undefined);
      resetAddChecklist();
      resetEditChecklist();
      setOpen?.(false);
      onSuccess?.();

      if (history.location.pathname !== Location.CHECKLIST_ALL) history.push(Location.CHECKLIST_ALL);

      if (isAddChecklistSuccess && selectedTemplateId && !disableSurvey) {
        triggerSurvey(SURVEY_KEYS.CHECKLIST_TEMPLATE_USED, {
          context: { templateId: selectedTemplateId, feature: 'checklist-template' },
        });
      }
    }
  }, [
    isAddChecklistSuccess,
    isEditChecklistSuccess,
    form,
    history,
    checklist,
    setOpen,
    onSuccess,
    resetAddChecklist,
    resetEditChecklist,
    selectedTemplateId,
    triggerSurvey,
    disableSurvey,
  ]);

  useEffect(() => {
    setPosition(open ? 'drawer' : 'default');
  }, [open, setPosition]);

  const isSavingOrSaved = form.formState.isSubmitting || isAddChecklistSuccess || isEditChecklistSuccess;
  const hasUnsavedChanges = open && form.formState.isDirty && !isSavingOrSaved;

  const {
    showDialog: showRouteLeaveDialog,
    confirmLeave,
    cancelLeave,
    handleBlockedNavigation,
  } = useConfirmBeforeLeave({
    hasUnsavedChanges,
    onLeave: () => {
      form.reset();
      setOpen?.(false);
    },
  });

  const requestClose = () => {
    if (form.formState.isDirty && !isSavingOrSaved) {
      setShowAlertDialogCancel(true);
      return;
    }
    form.reset();
    setOpen?.(false);
    Analytics.track('Checklist Drawer Closed', { mode: checklist ? 'edit' : 'create' });
  };

  const handleConfirmDiscard = () => {
    setShowAlertDialogCancel(false);
    setTimeout(() => {
      form.reset();
      setOpen?.(false);
      if (typeof document !== 'undefined') {
        document.body.style.pointerEvents = '';
      }
    }, 0);
    Analytics.track('Checklist Drawer Closed', { mode: checklist ? 'edit' : 'create' });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setOpen?.(true);
      setPosition('drawer');
      Analytics.track('Checklist Drawer Opened', { mode: checklist ? 'edit' : 'create' });
      return;
    }
    requestClose();
  };

  const isFormStep = step === 'form' || !!checklist;

  return (
    <>
      <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
      <Drawer handleOnly open={open} onOpenChange={handleOpenChange}>
        {!(checklist || hideTrigger) && (
          <DrawerTrigger asChild>
            <Button variant='outlined' size='sm' startIcon={<Icon name='plus' />} data-testid='create-checklist-button'>
              {t('create_checklist')}
            </Button>
          </DrawerTrigger>
        )}
        <DrawerContent>
          <Form form={form} onSubmit={handleSubmit} className='size-full gap-0'>
            <DrawerHeader className='w-full bg-white'>
              <div className='flex w-full flex-col gap-3'>
                <div className='flex w-full items-center justify-between'>
                  <DrawerTitle>{checklist ? t('edit_checklist') : t('create_checklist')}</DrawerTitle>
                  <DrawerClose />
                </div>
                {!checklist && (
                  <Stepper
                    defineSteps={[
                      { id: 'choose', title: t('templates.step_choose_title') },
                      { id: 'form', title: t('templates.step_customize_title') },
                    ]}
                  >
                    <StepperHorizontalTimelineHeader />
                    <StepperController id={step} />
                  </Stepper>
                )}
              </div>
            </DrawerHeader>

            {checklist && (
              <div className='mt-2 flex w-full justify-end px-5 py-2'>
                <ChecklistStatusToggle isActive={form.watch('active')} onActiveChange={(active) => form.setValue('active', active, { shouldDirty: true })} />
              </div>
            )}

            {step === 'choose' && !checklist && <ChecklistTemplateStep selectedId={selectedTemplateId} onSelectedIdChange={setSelectedTemplateId} />}

            {isFormStep && (
              <DraggableListContainer className='h-full w-full p-5'>
                <ChecklistName />
                <ChecklistVehiclesFilters />
                <Divider className='mt-2.5 mb-5' />
                <ChecklistItems />
              </DraggableListContainer>
            )}

            <DrawerFooter className='justify-between'>
              <Button
                variant='ghost'
                onClick={() => {
                  Analytics.track('Checklist Drawer Cancel Button Clicked', {
                    mode: checklist ? 'edit' : 'create',
                  });
                  requestClose();
                }}
              >
                {t('cancel')}
              </Button>
              <div className='flex gap-2.5'>
                {isFormStep && !checklist && <Button variant='outlined' onClick={() => setStep('choose')} icon={<Icon name='chevron-left' />} />}
                {step === 'choose' ? (
                  <Button onClick={() => handleTemplateSelect(selectedTemplateId)}>{t('templates.continue')}</Button>
                ) : (
                  <ChecklistSaveButton checklist={checklist} onSubmit={handleSubmit} />
                )}
              </div>
            </DrawerFooter>
          </Form>
        </DrawerContent>

        <AlertDialogCancel
          open={showAlertDialogCancel}
          title={t('close_drawer_without_saving.title')}
          description={t('close_drawer_without_saving.description')}
          confirmLabel={t('close_drawer_without_saving.confirm_label')}
          onOpenChange={setShowAlertDialogCancel}
          onConfirm={handleConfirmDiscard}
        />

        <AlertDialogCancel
          open={showRouteLeaveDialog}
          title={t('close_drawer_without_saving.title')}
          description={t('close_drawer_without_saving.description')}
          confirmLabel={t('close_drawer_without_saving.confirm_label')}
          onOpenChange={(isOpen) => {
            if (!isOpen) cancelLeave();
          }}
          onConfirm={confirmLeave}
        />
      </Drawer>
    </>
  );
};

export default ChecklistDrawer;
